import React, { Component } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import _each from 'lodash/each'
import { parse as _parseQuery } from 'qs'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

import Marker from '../components/Marker'
import GeolocationAccuracyDisplay from '../components/GeolocationAccuracyDisplay'
import { roundAccuracy, splitBounds } from '../utils/geolocation'

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN
const mapboxStyleUrl = 'mapbox://styles/jinksi/cik86cb1100gen8m758f5a2ro'

const MapWrap = styled.div`
  height: 100%;

  ${props => {
    if (props.inset) {
      return css`
        height: 50vh;
        position: relative;
        background: white;
        margin: 0;
        color: black;

        &:after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          box-shadow: 0 0 1rem rgba(0, 0, 0, 0.3) inset;
          z-index: 10;
        }
      `
    }
  }};
`

class MapGL extends Component {
  static defaultProps = {
    defaultBounds:
      '153.2270711003045,-28.263917097117464,153.31922765048108,-28.138441450608497',
    maxBounds:
      '153.10074414385213,-28.4024509659868,153.6806200815048,-27.687802816104586'
  }
  constructor (props) {
    super(props)
    this.state = {
      style: mapboxStyleUrl,
      zoom: this.props.zoom || 12,
      zoomEnable: this.props.zoomEnable || false,
      doubleClickZoom: this.props.doubleClickZoom || false,
      controls: this.props.controls || false,
      interactive: this.props.interactive || false,
      geolocate: this.props.geolocate || false,
      center: this.props.center || [153.2731493753928, -28.201197692223978],
      geolocationAccuracy: false,
      maxBounds: new mapboxgl.LngLatBounds(splitBounds(props.maxBounds)),
      bounds:
        props.geoBounds &&
        new mapboxgl.LngLatBounds(splitBounds(props.geoBounds)),
      trackStyle: {
        layout: {
          'line-join': 'bevel',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#f97f7f',
          'line-width': 2
        }
      },
      tracks: []
    }
    this._map = null
    this.mapEl = null
    this.markers = []
  }

  componentDidMount () {
    this.createMap()
    this.checkQueryString()
  }

  componentWillUnmount () {
    this.removeMap()
    this.removeMapListeners()
  }

  componentDidUpdate (prevProps, prevState) {
    if (this._map && this.state.interactive && !this.state.listeners) {
      console.log('setlisteners')
      this.setMapListeners()
    }
    if (this._map && this.state.tracks.length > 0) {
      console.log('addLayer')
      this.state.tracks.map(track => {
        return this._map.addLayer(track)
      })
    }
  }

  shouldComponentUpdate (nextProps, nextState) {
    if (!this.markers.length) return true
    if (this.state.tracks.length !== nextState.tracks.length) return true
    if (this.state.geolocationAccuracy !== nextState.geolocationAccuracy) {
      return true
    }
    return false
  }

  createMap = () => {
    this._map = new mapboxgl.Map({
      ...this.state,
      container: this.mapEl
    })
    if (this.state.controls) {
      this._map.addControl(new mapboxgl.NavigationControl())
    }
    if (!this.state.zoomEnable) {
      this._map.scrollZoom.disable()
    }
    if (this.state.geolocate) {
      const geoLocateControl = new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true,
          maximumAge: 0
        },
        trackUserLocation: true
      })
      this._map.addControl(geoLocateControl)
      geoLocateControl.on('geolocate', this.displayGeolocationAccuracy)
      geoLocateControl.on('error', console.error)
    }
    this._map.dragRotate.disable()
    this.resetMapBounds()
    this.setState({})
  }

  displayGeolocationAccuracy = e => {
    if (!e || !e.coords) {
      return this.setState({
        geolocationAccuracy: false
      })
    }

    const accuracyStr = roundAccuracy(e.coords.accuracy)
    console.log(`Location accuracy ${accuracyStr}`)
    return this.setState({
      geolocationAccuracy: accuracyStr
    })
  }

  removeMap = () => {
    this._map.remove()
  }

  checkQueryString = () => {
    const qs = _parseQuery(window.location.search)
    const bounds = qs['bounds'] || qs['?bounds']
    if (bounds) {
      this.setState({ bounds: splitBounds(bounds) }, () => {
        this.resetMapBounds()
      })
    }
  }

  setMapListeners = () => {
    this._map.on('click', this.handleClick)
    this._map.on('dblclick', this.handleDoubleClick)
    this._map.on('load', this.calculateGeoJSON)
    this.setState({ listeners: true })
  }

  removeMapListeners = () => {
    this._map.off('click', this.handleClick)
    this._map.off('dblclick', this.handleDoubleClick)
  }

  handleClick = e => {
    this.markers.forEach(
      marker => marker && marker.setState({ infoVisible: false })
    )
  }

  handleDoubleClick = e => {
    this._map.easeTo({
      center: e.lngLat,
      zoom: this._map.getZoom() + 1,
      duration: 100
    })
  }

  handleMarkerClick = (e, marker) => {
    this.markers.forEach(
      marker => marker && marker.setState({ infoVisible: false })
    )
    marker.setState({ infoVisible: true })
    this._map.flyTo({
      zoom: this._map.getZoom() > 14 ? this._map.getZoom() : 14,
      center: marker.state.center,
      speed: 0.4
    })
  }

  handleMarkerMouseOver = (e, marker) => {
    if (['svg', 'path'].indexOf(e.target.nodeName) < 0) return false
    this.markers.forEach(
      marker => marker && marker.setState({ infoVisible: false })
    )
    marker.setState({ infoVisible: true })
  }

  resetMapBounds = () => {
    if (this.state.bounds) this._map.fitBounds(this.state.bounds)
  }

  renderMarkers = () => {
    if (!this.props.locations || !this.props.locations.length) return
    this.markers = []

    return this.props.locations.map((location, index) => (
      <Marker
        key={location.fields.slug}
        location={location}
        map={this._map}
        handleClick={this.handleMarkerClick}
        handleMouseOver={this.handleMarkerMouseOver}
        ref={marker => this.markers.push(marker)}
        showPopups={this.state.interactive || this.props.inset}
        infoVisible={this.props.inset && index === 0}
        infoLink={!this.props.inset}
      />
    ))
  }

  calculateGeoJSON = () => {
    if (!this._map) return null
    if (!this.props.locations || !this.props.locations.length) return null
    if (!this._map.isStyleLoaded()) {
      console.log('style loading')
    } else {
      this.loadTracksGeoJSON()
    }
  }

  loadTracksGeoJSON = () => {
    let tracksArray = []
    const tracks = this.props.tracks
    if (tracks && tracks.length > 0) {
      _each(tracks, (track, index) => {
        if (track.fields.geojson) {
          this._map.addSource(track.fields.slug, {
            type: 'geojson',
            data: track.fields.geojson
          })
          tracksArray.push({
            id: track.fields.slug,
            type: 'line',
            source: track.fields.slug,
            layout: this.state.trackStyle.layout,
            paint: this.state.trackStyle.paint
          })
        }
      })
    }
    this.setState({ tracks: tracksArray })
  }

  toggleGeoJSON = () => {
    _each(this.state.tracks, track => {
      console.log(track)
    })
  }

  render () {
    return (
      <MapWrap
        innerRef={map => {
          this.mapEl = map
        }}
        inset={this.props.inset}
      >
        {this._map && this.renderMarkers()}
        {this.state.geolocationAccuracy && (
          <GeolocationAccuracyDisplay>
            {this.state.geolocationAccuracy}
          </GeolocationAccuracyDisplay>
        )}
      </MapWrap>
    )
  }
}

MapGL.propTypes = {
  locations: PropTypes.array,
  zoom: PropTypes.number,
  center: PropTypes.array,
  interactive: PropTypes.bool,
  geolocate: PropTypes.bool,
  inset: PropTypes.bool,
  controls: PropTypes.bool,
  zoomEnable: PropTypes.bool
}

export default MapGL



// WEBPACK FOOTER //
// ./src/components/MapGL.js